/**
 * Sembrado y simulación de stream en vivo para la Capa Operacional
 * AI-First (modo DEMO, R17.1/R17.5).
 *
 * El Ticker_Operacional (tarea 5.2) y el hook {@link useRealtime} reflejan
 * los Eventos_Operacionales que el Realtime Hub (`realtimeHub.ts`, tarea
 * 5.1) entrega; ese Hub se **alimenta del Event_Store** (`eventStore.ts`,
 * tarea 1.3). Para que las vistas (LiveOperations, AlertCenter,
 * OperationalMap) tengan eventos que mostrar en modo demo, este módulo:
 *
 *   1. **Siembra** el Event_Store con un backlog de Eventos_Operacionales
 *      derivados de las **alarmas reales** de Antofagasta
 *      (`/data/alarmas.json`), reutilizando los mismos JSONs que ya
 *      consumen OperationalMap y PriorityCases (R17 — no duplica datos);
 *   2. arranca un **simulador de stream** que, cada pocos segundos, emite
 *      un nuevo Evento_Operacional con marca de tiempo actual (≤2s, R7.4),
 *      tanto al Event_Store (persistencia/replay) como al Hub (fan-out en
 *      vivo). Así el Ticker muestra append incremental real.
 *
 * Todo es **idempotente** y singleton por sesión: sembrar y arrancar el
 * stream varias veces no duplica eventos ni temporizadores. La siembra se
 * hace **antes** de que el Hub hidrate su log (en la primera suscripción),
 * de modo que el backlog aparezca sin doble conteo; los eventos en vivo se
 * emiten directamente al Hub y se persisten en el Event_Store sin que la
 * hidratación los vuelva a leer.
 */
import type {
  EntidadRef,
  EventoOperacional,
  IsoTimestamp,
} from "../api/contracts";
import { runtime } from "../api/runtime";
import { loadRealData, type RealAlarma } from "../api/realData";
import { ensureDemoProviders } from "../lib/demoRuntime";
import type { EventoEntrada } from "../lib/eventStore";
import { ensureRealtimeProvider, getRealtimeHub } from "./realtimeHub";

/** Intervalo del simulador de stream en vivo (ms). */
export const INTERVALO_STREAM_MS = 3_500;

/** Máximo de eventos del backlog sembrados desde alarmas reales. */
const MAX_SEED = 60;

/** Mapea una bandera de alarma real al tipo de Evento_Operacional. */
interface DefAlarma {
  /** Nombre del campo booleano en {@link RealAlarma}. */
  campo: keyof RealAlarma;
  /** Tipo de Evento_Operacional (∈ TIPOS_EVENTO_CONOCIDOS del Hub). */
  tipo: string;
  /** Tipo de entidad afectada. */
  entidad: EntidadRef["tipo"];
  /** Etiqueta legible del estado. */
  estado: string;
}

/**
 * Tabla de derivación alarma → Evento_Operacional. Las fugas/bursts se
 * proyectan como Alertas (consumidas por AlertCenter); el resto como
 * Anomalias IoT (consumidas por el feed operacional).
 */
const DEFS_ALARMA: DefAlarma[] = [
  { campo: "alarm_burst", tipo: "alerta_generada", entidad: "Alerta", estado: "burst" },
  { campo: "alarm_leakage", tipo: "alerta_generada", entidad: "Alerta", estado: "fuga" },
  { campo: "alarm_backflow", tipo: "anomalia_detectada", entidad: "Anomalia", estado: "backflow" },
  { campo: "alarm_dry", tipo: "anomalia_detectada", entidad: "Anomalia", estado: "seco" },
  { campo: "alarm_manipulation", tipo: "anomalia_detectada", entidad: "Anomalia", estado: "manipulacion" },
];

/** Normaliza un timestamp de alarma a ISO-8601 UTC; usa "ahora" si no es válido. */
function normalizarTs(raw: string | undefined): IsoTimestamp {
  if (raw) {
    const t = Date.parse(raw);
    if (Number.isFinite(t)) return new Date(t).toISOString();
  }
  return new Date().toISOString();
}

/**
 * Deriva el backlog de Eventos_Operacionales (sin los campos de cadena
 * hash, que el Event_Store calcula) a partir de las alarmas reales.
 * Determinista respecto al orden de las alarmas; ordenado por `ts`.
 */
export function derivarEventosDeAlarmas(alarmas: RealAlarma[]): EventoEntrada[] {
  const eventos: EventoEntrada[] = [];
  for (const a of alarmas) {
    const ts = normalizarTs(a.root_timestamp);
    for (const def of DEFS_ALARMA) {
      if (a[def.campo] !== true) continue;
      const id = `${def.estado}-${a.device}`;
      eventos.push({
        tipo: def.tipo,
        entidad_ref: { tipo: def.entidad, id },
        actor: "ingesta",
        ts,
        payload: {
          estado: def.estado,
          device: a.device,
          dma_idx: a.dma_idx,
          volumen: a.total_volume,
        },
        correlation_id: `corr-seed-${def.estado}-${a.device}`,
      });
    }
  }
  eventos.sort((x, y) => (x.ts < y.ts ? -1 : x.ts > y.ts ? 1 : 0));
  return eventos;
}

// ---------------------------------------------------------------------------
// Estado singleton de la sesión
// ---------------------------------------------------------------------------

let seedPromise: Promise<void> | null = null;
let streamTimer: ReturnType<typeof setInterval> | null = null;
/** Plantillas de eventos para el simulador en vivo (entidades reales). */
let plantillasVivo: EventoEntrada[] = [];
let tickVivo = 0;

/**
 * Siembra el Event_Store con el backlog derivado de las alarmas reales
 * (idempotente). Debe ejecutarse **antes** de que el Hub hidrate su log
 * (primera suscripción), por lo que el llamador debe `await` esta promesa
 * antes de montar el Ticker.
 */
export function ensureLiveDemoSeed(): Promise<void> {
  if (seedPromise) return seedPromise;
  seedPromise = (async () => {
    ensureDemoProviders();
    ensureRealtimeProvider();

    const eventStore = runtime.eventStore();

    // Si el Event_Store ya tiene eventos (sembrado por otra vista o por
    // una recarga), no duplicamos el backlog.
    const hub = getRealtimeHub();
    const real = await loadRealData();
    const alarmas = real?.alarmas ?? [];
    const derivados = derivarEventosDeAlarmas(alarmas).slice(0, MAX_SEED);

    // Guarda plantillas para el simulador en vivo (reusa entidades reales).
    plantillasVivo = derivados.length > 0 ? derivados : plantillaPorDefecto();

    // Solo siembra si el log global del Hub está vacío (idempotencia).
    const tieneEventos = await primerEventoExiste(eventStore);
    if (!tieneEventos) {
      for (const e of derivados) {
        await eventStore.append(e);
      }
    }
    // Asegura que el Hub hidrate el backlog ya sembrado.
    await hub.hydrate();
  })();
  return seedPromise;
}

/** ¿Existe al menos un evento en el log del Event_Store? */
async function primerEventoExiste(
  eventStore: ReturnType<typeof runtime.eventStore>,
): Promise<boolean> {
  for await (const _e of eventStore.streamFrom(1)) {
    void _e;
    return true;
  }
  return false;
}

/** Plantilla mínima cuando no hay alarmas reales disponibles. */
function plantillaPorDefecto(): EventoEntrada[] {
  const ahora = new Date().toISOString();
  return [
    {
      tipo: "alerta_generada",
      entidad_ref: { tipo: "Alerta", id: "demo-001" },
      actor: "ingesta",
      ts: ahora,
      payload: { estado: "fuga" },
      correlation_id: "corr-demo-001",
    },
    {
      tipo: "anomalia_detectada",
      entidad_ref: { tipo: "Anomalia", id: "demo-002" },
      actor: "ingesta",
      ts: ahora,
      payload: { estado: "mnf" },
      correlation_id: "corr-demo-002",
    },
  ];
}

/**
 * Arranca el simulador de stream en vivo (idempotente). Cada
 * {@link INTERVALO_STREAM_MS} emite un nuevo Evento_Operacional con marca
 * de tiempo actual: lo persiste en el Event_Store y lo difunde por el Hub
 * (append incremental ≤2s, R7.4). Devuelve una función para detenerlo.
 */
export function startLiveDemoStream(): () => void {
  if (streamTimer) return stopLiveDemoStream;

  streamTimer = setInterval(() => {
    void emitirEventoVivo();
  }, INTERVALO_STREAM_MS);

  return stopLiveDemoStream;
}

/** Detiene el simulador de stream en vivo. */
export function stopLiveDemoStream(): void {
  if (streamTimer) {
    clearInterval(streamTimer);
    streamTimer = null;
  }
}

/**
 * Emite un Evento_Operacional "en vivo": reutiliza de forma rotatoria una
 * entidad real de las plantillas, lo sella con la hora actual, lo persiste
 * en el Event_Store y lo difunde por el Hub para que el Ticker lo refleje.
 */
async function emitirEventoVivo(): Promise<void> {
  const plantillas = plantillasVivo.length > 0 ? plantillasVivo : plantillaPorDefecto();
  const base = plantillas[tickVivo % plantillas.length];
  tickVivo += 1;

  const entrada: EventoEntrada = {
    ...base,
    ts: new Date().toISOString(),
    payload: { ...base.payload, live: true },
    correlation_id: `corr-live-${Date.now().toString(16)}`,
  };

  const eventStore = runtime.eventStore();
  const res = await eventStore.append(entrada);
  if (!res.ok) return;

  // Reconstruye el Evento_Operacional completo para difundirlo por el Hub.
  const completo: EventoOperacional = {
    ...entrada,
    event_id: res.value.event_id,
    seq: res.value.seq,
    prev_hash: res.value.prev_hash,
    payload_hash: res.value.hash,
  };
  getRealtimeHub().emit(completo);
}
